/**
 * Life4Earth V2 — Project Issues API Adapter
 *
 * CRUD for the Issues Register.
 *
 * Endpoint: /api/projects/{guid}/issues
 *
 * Epic: DEV-683
 */

import { apiFetch } from "./client";

// ── Types ─────────────────────────────────────────────────────

export type IssueSeverity = "blocking" | "warning" | "informational";
export type IssueCategory =
  | "operational"
  | "technical"
  | "financial"
  | "environmental"
  | "regulatory"
  | "safety";
export type IssueStatusValue = "open" | "in_progress" | "resolved" | "closed";

export interface ProjectIssueResponse {
  id: string;
  /** Full-project owner. Null on mini-project rows (DEV-1141). */
  projectGuid: string | null;
  /** Mini-project owner. Null on full-project rows (DEV-1141). */
  miniProjectGuid?: string | null;
  /** Polymorphic discriminator: 'project' | 'mini_project' (DEV-1141). */
  contentType?: string;
  title: string;
  description: string | null;
  severity: string;
  category: string;
  resolutionPlan: string | null;
  ownerRoleSlotGuid: string | null;
  ownerUserGuid: string | null;
  dueDate: string | null;
  status: string;
  isKeyIssue: boolean;
  linkedTaskIds: string[];
  sourceFlagId: string | null;
  sourceRiskId: string | null;
  bypassRiskNote: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  // HSE subtype fields (DEV-822) — present on every response, populated
  // only when the row's subtype = 'hse_incident'.
  subtype?: string;
  incidentDate?: string | null;
  hseCategory?: string | null;
  severityHse?: string | null;
  confidential?: boolean;
  // File attachments (DEV-822 #126). MinIO object keys; serve via
  // /api/FileStorage/file/{key}.
  attachmentFileKeys?: string[];
}

export interface CreateProjectIssueRequest {
  title: string;
  description?: string;
  severity?: string;
  category?: string;
  resolutionPlan?: string;
  ownerRoleSlotGuid?: string;
  dueDate?: string;
  linkedTaskIds?: string[];
  sourceFlagId?: string;
  sourceRiskId?: string;
  bypassRiskNote?: string;
  // HSE subtype fields (DEV-822). Required when subtype='hse_incident'.
  subtype?: 'standard' | 'hse_incident';
  incidentDate?: string; // ISO datetime; backend treats as immutable after first save
  hseCategory?: 'health' | 'safety' | 'environment';
  severityHse?: 'near-miss' | 'first-aid' | 'recordable' | 'lost-time' | 'fatality' | 'minor' | 'reportable' | 'major' | 'catastrophic';
  confidential?: boolean;
  /** MinIO object keys for files attached to this issue (DEV-822 #126). */
  attachmentFileKeys?: string[];
}

export interface UpdateProjectIssueRequest {
  title?: string;
  description?: string;
  severity?: string;
  category?: string;
  resolutionPlan?: string;
  ownerRoleSlotGuid?: string;
  dueDate?: string;
  status?: string;
  isKeyIssue?: boolean;
  linkedTaskIds?: string[];
  // HSE-specific (DEV-822). incidentDate is intentionally absent — it's
  // immutable after first save (the controller rejects attempts to modify it).
  hseCategory?: string;
  severityHse?: string;
  confidential?: boolean;
  /** Replace the attachment list. NULL = leave unchanged. (DEV-822 #126) */
  attachmentFileKeys?: string[];
}

// ── API Functions ─────────────────────────────────────────────

/** Fetch all issues for a project. */
export async function fetchProjectIssues(
  projectGuid: string
): Promise<ProjectIssueResponse[]> {
  return apiFetch<ProjectIssueResponse[]>(
    `/api/projects/${projectGuid}/issues`
  );
}

/** Fetch a single issue by ID. */
export async function fetchProjectIssue(
  projectGuid: string,
  issueId: string
): Promise<ProjectIssueResponse> {
  return apiFetch<ProjectIssueResponse>(
    `/api/projects/${projectGuid}/issues/${issueId}`
  );
}

/** Create a new issue. */
export async function createProjectIssue(
  projectGuid: string,
  req: CreateProjectIssueRequest
): Promise<ProjectIssueResponse> {
  return apiFetch<ProjectIssueResponse>(
    `/api/projects/${projectGuid}/issues`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Patch-update an issue. */
export async function updateProjectIssue(
  projectGuid: string,
  issueId: string,
  req: UpdateProjectIssueRequest
): Promise<ProjectIssueResponse> {
  return apiFetch<ProjectIssueResponse>(
    `/api/projects/${projectGuid}/issues/${issueId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Delete an issue. */
export async function deleteProjectIssue(
  projectGuid: string,
  issueId: string
): Promise<void> {
  await apiFetch<void>(
    `/api/projects/${projectGuid}/issues/${issueId}`,
    { method: "DELETE" }
  );
}

// ── Mini-Project HSE incident CRUD (DEV-1141) ─────────────────────
//
// Same DTOs as the full-project issue endpoints, but routed at the
// polymorphic mini-project owner: /api/mini-projects/{guid}/hse-incidents.
// The backend forces subtype='hse_incident', so these only carry HSE rows.
// Used by the Report/Edit/DrillDown modals when given a miniProjectGuid
// instead of a projectGuid.

/** Fetch a single MP HSE incident by ID. */
export async function fetchMiniProjectHseIncident(
  miniProjectGuid: string,
  issueId: string
): Promise<ProjectIssueResponse> {
  return apiFetch<ProjectIssueResponse>(
    `/api/mini-projects/${miniProjectGuid}/hse-incidents/${issueId}`
  );
}

/** Report a new HSE incident on a mini-project. */
export async function createMiniProjectHseIncident(
  miniProjectGuid: string,
  req: CreateProjectIssueRequest
): Promise<ProjectIssueResponse> {
  return apiFetch<ProjectIssueResponse>(
    `/api/mini-projects/${miniProjectGuid}/hse-incidents`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...req, subtype: "hse_incident" }),
    }
  );
}

/** Patch-update an MP HSE incident. */
export async function updateMiniProjectHseIncident(
  miniProjectGuid: string,
  issueId: string,
  req: UpdateProjectIssueRequest
): Promise<ProjectIssueResponse> {
  return apiFetch<ProjectIssueResponse>(
    `/api/mini-projects/${miniProjectGuid}/hse-incidents/${issueId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Delete an MP HSE incident (system-admin only server-side). */
export async function deleteMiniProjectHseIncident(
  miniProjectGuid: string,
  issueId: string
): Promise<void> {
  await apiFetch<void>(
    `/api/mini-projects/${miniProjectGuid}/hse-incidents/${issueId}`,
    { method: "DELETE" }
  );
}
